import React, { useState, useEffect, useCallback } from 'react';
import { Bell, Check, Calendar, AlertCircle, CheckCircle, Info, AlertTriangle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/layout/Layout';
import mockApi from '../services/mockApi';

const ClienteNotificacionesPage = () => {
  const { user } = useAuth();
  const [notificaciones, setNotificaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroLeida, setFiltroLeida] = useState('todas');
  const [filtroTipo, setFiltroTipo] = useState('todos');

  const cargarNotificaciones = useCallback(async () => {
    try {
      setLoading(true);
      const clienteRes = await mockApi.getClienteByEmail(user.email);
      const response = await mockApi.getNotificacionesCliente(clienteRes.data.id);
      setNotificaciones(response.data.notificaciones || []);
    } catch (error) {
      console.error('Error al cargar notificaciones:', error);
      setNotificaciones([]);
    } finally {
      setLoading(false);
    }
  }, [user.email]);

  useEffect(() => {
    cargarNotificaciones();
  }, [cargarNotificaciones]);

  const marcarComoLeida = async (notificacionId) => {
    try {
      const clienteRes = await mockApi.getClienteByEmail(user.email);
      await mockApi.marcarNotificacionLeida(clienteRes.data.id, notificacionId);
      setNotificaciones(notificaciones.map(n =>
        n.id === notificacionId ? { ...n, leida: true } : n
      ));
    } catch (error) {
      console.error('Error al marcar notificación:', error);
    }
  };

  const marcarTodasComoLeidas = async () => {
    try {
      const clienteRes = await mockApi.getClienteByEmail(user.email);
      const noLeidas = notificaciones.filter(n => !n.leida);
      for (const notif of noLeidas) {
        await mockApi.marcarNotificacionLeida(clienteRes.data.id, notif.id);
      }
      setNotificaciones(notificaciones.map(n => ({ ...n, leida: true })));
    } catch (error) {
      console.error('Error al marcar todas:', error);
    }
  };

  const notificacionesFiltradas = notificaciones.filter(notif => {
    const cumpleFiltroLeida =
      filtroLeida === 'todas' ||
      (filtroLeida === 'no_leidas' && !notif.leida) ||
      (filtroLeida === 'leidas' && notif.leida);

    const cumpleFiltroTipo = filtroTipo === 'todos' || notif.tipo === filtroTipo;

    return cumpleFiltroLeida && cumpleFiltroTipo;
  });

  const estadisticas = {
    total: notificaciones.length,
    noLeidas: notificaciones.filter(n => !n.leida).length,
    leidas: notificaciones.filter(n => n.leida).length,
    importantes: notificaciones.filter(n => n.tipo === 'alerta').length
  };

  const getTipoConfig = (tipo) => {
    const configs = {
      info: {
        color: 'bg-info-50 text-info-700',
        icon: Info,
        label: 'Información'
      },
      alerta: {
        color: 'bg-danger-50 text-danger-700',
        icon: AlertCircle,
        label: 'Alerta'
      },
      exito: {
        color: 'bg-success-50 text-success-700',
        icon: CheckCircle,
        label: 'Éxito'
      },
      aviso: {
        color: 'bg-warning-50 text-warning-700',
        icon: AlertTriangle,
        label: 'Aviso'
      }
    };
    return configs[tipo] || configs.info;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="rounded-full h-12 w-12 border-4 border-primary-200 border-t-primary-700 mx-auto" style={{ animation: 'spin 1s linear infinite' }}></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Notificaciones
            </h1>
            <p className="text-gray-500 mt-1">Mantente informado sobre tu garantía y averías</p>
          </div>
          {estadisticas.noLeidas > 0 && (
            <button
              onClick={marcarTodasComoLeidas}
              className="flex items-center gap-2 px-6 py-3 bg-primary-700 text-white rounded-lg hover:bg-primary-800 font-semibold"
            >
              <Check className="w-5 h-5" />
              <span>Marcar todas como leídas</span>
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg p-6 border border-gray-200 border-l-4 border-l-primary-700 shadow-soft">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">Total</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{estadisticas.total}</p>
              </div>
              <Bell className="w-10 h-10 text-primary-700" />
            </div>
          </div>
          <div className="bg-white rounded-lg p-6 border border-gray-200 border-l-4 border-l-warning-500 shadow-soft">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">No Leídas</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{estadisticas.noLeidas}</p>
              </div>
              <AlertCircle className="w-10 h-10 text-warning-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg p-6 border border-gray-200 border-l-4 border-l-success-500 shadow-soft">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">Leídas</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{estadisticas.leidas}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-success-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg p-6 border border-gray-200 border-l-4 border-l-danger-500 shadow-soft">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">Importantes</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{estadisticas.importantes}</p>
              </div>
              <AlertTriangle className="w-10 h-10 text-danger-500" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-soft">
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex gap-2">
              <button
                onClick={() => setFiltroLeida('todas')}
                className={`px-4 py-3 rounded-lg font-semibold ${
                  filtroLeida === 'todas'
                    ? 'bg-primary-700 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Todas
              </button>
              <button
                onClick={() => setFiltroLeida('no_leidas')}
                className={`px-4 py-3 rounded-lg font-semibold ${
                  filtroLeida === 'no_leidas'
                    ? 'bg-warning-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                No Leídas ({estadisticas.noLeidas})
              </button>
              <button
                onClick={() => setFiltroLeida('leidas')}
                className={`px-4 py-3 rounded-lg font-semibold ${
                  filtroLeida === 'leidas'
                    ? 'bg-success-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Leídas
              </button>
            </div>
            <div className="flex gap-2 ml-auto">
              <button
                onClick={() => setFiltroTipo('todos')}
                className={`px-4 py-3 rounded-lg font-semibold ${
                  filtroTipo === 'todos'
                    ? 'bg-gray-700 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Todos los tipos
              </button>
              <button
                onClick={() => setFiltroTipo('alerta')}
                className={`px-4 py-3 rounded-lg font-semibold ${
                  filtroTipo === 'alerta'
                    ? 'bg-danger-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Alertas
              </button>
            </div>
          </div>

          <div className="space-y-3">
            {notificacionesFiltradas.length === 0 ? (
              <div className="text-center py-12">
                <Bell className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 font-medium">No hay notificaciones</p>
              </div>
            ) : (
              notificacionesFiltradas.map((notificacion) => {
                const tipoConfig = getTipoConfig(notificacion.tipo);
                const IconoTipo = tipoConfig.icon;

                return (
                  <div
                    key={notificacion.id}
                    className={`bg-white rounded-lg p-5 border border-gray-200 shadow-card ${
                      !notificacion.leida ? 'border-l-4 border-l-primary-700 bg-primary-50' : ''
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${tipoConfig.color} flex-shrink-0`}>
                        <IconoTipo className="w-6 h-6" />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4 mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-1">
                              <h3 className={`font-bold text-gray-900 ${!notificacion.leida ? 'text-lg' : ''}`}>
                                {notificacion.titulo}
                              </h3>
                              {!notificacion.leida && (
                                <span className="w-2 h-2 bg-primary-700 rounded-full"></span>
                              )}
                            </div>
                            <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${tipoConfig.color}`}>
                              {tipoConfig.label}
                            </span>
                          </div>

                          {!notificacion.leida && (
                            <button
                              onClick={() => marcarComoLeida(notificacion.id)}
                              className="flex items-center gap-2 px-3 py-2 bg-success-600 text-white rounded-lg hover:bg-success-700 text-sm font-semibold"
                            >
                              <Check className="w-4 h-4" />
                              <span>Marcar leída</span>
                            </button>
                          )}
                        </div>

                        <p className="text-gray-600 mb-3">{notificacion.mensaje}</p>

                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Calendar className="w-4 h-4" />
                          <span>{new Date(notificacion.fecha).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="bg-info-50 rounded-lg p-6 border border-info-200">
          <div className="flex items-start gap-4">
            <Bell className="w-6 h-6 text-info-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-info-900 mb-2">Sobre las notificaciones</h3>
              <ul className="text-sm text-info-700 space-y-2">
                <li>Recibirás notificaciones sobre el estado de tus averías</li>
                <li>Te avisaremos cuando tu garantía esté próxima a vencer</li>
                <li>Las alertas importantes requieren tu atención inmediata</li>
                <li>Puedes configurar tus preferencias de notificaciones en Configuración</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ClienteNotificacionesPage;
